import React, { useState, useEffect, useMemo } from 'react';
import { getAllClients, deleteClient } from '../services/api';
import { ClientListProps, Client, ClientStatus } from '../types';

type TabFilter = 'all' | 'draft' | 'financials' | 'lenders_submissions' | 'funding_booking' | 'final';

const TAB_NAMES: Record<TabFilter, string> = {
    all: 'All',
    draft: 'Draft',
    financials: 'Financials',
    lenders_submissions: 'Lenders & Submissions',
    funding_booking: 'Funding & Booking',
    final: 'Final',
};

const TAB_TO_STATUS: Record<TabFilter, ClientStatus | null> = {
    all: null,
    draft: 'draft',
    financials: 'financials',
    final: 'completed',
    lenders_submissions: 'lenders_submissions',
    funding_booking: 'funding_booking',
};

const STATUS_PROGRESS_MESSAGES: Record<ClientStatus, string> = {
    draft: 'Ready to add financial statements',
    financials: 'Files uploaded, ready for evaluation',
    completed: '',
    lenders_submissions: 'Submitted to lenders',
    funding_booking: 'Funding booking in progress',
};

const STATUS_BADGE_COLORS: Record<ClientStatus, string> = {
    draft: 'bg-yellow-400',
    financials: 'bg-blue-400',
    completed: 'bg-green-400',
    lenders_submissions: 'bg-purple-400',
    funding_booking: 'bg-indigo-400',
};

const STATUS_BADGE_LABELS: Record<ClientStatus, string> = {
    draft: 'DRAFT',
    financials: 'IN PROGRESS',
    completed: 'COMPLETED',
    lenders_submissions: 'LENDERS SUBMISSIONS',
    funding_booking: 'FUNDING BOOKING',
};

interface ButtonConfig {
    label: string;
    className: string;
}

const STATUS_BUTTON_CONFIG: Record<ClientStatus, ButtonConfig> = {
    draft: {
        label: '📝 Edit Draft',
        className: 'btn bg-yellow-600 hover:bg-yellow-700 text-white border-yellow-600 btn-sm font-bold w-32',
    },
    financials: {
        label: '⚡ Continue',
        className: 'btn bg-blue-600 hover:bg-blue-700 text-white border-blue-600 btn-sm font-bold w-32',
    },
    completed: {
        label: '📊 View Details',
        className: 'btn bg-green-600 hover:bg-green-700 text-white border-green-600 btn-sm font-bold w-32',
    },
    lenders_submissions: {
        label: '📤 View',
        className: 'btn bg-purple-600 hover:bg-purple-700 text-white border-purple-600 btn-sm font-bold w-32',
    },
    funding_booking: {
        label: '💰 View',
        className: 'btn bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600 btn-sm font-bold w-32',
    },
};

const ClientList: React.FC<ClientListProps> = ({ onSelectClient, onNewClient }) => {
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabFilter>('all');

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async (): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await getAllClients();
            setClients(data.clients || []);
        } catch (err) {
            setError((err as Error).message || 'Failed to load clients');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (clientId: string): Promise<void> => {
        if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
            return;
        }

        try {
            await deleteClient(clientId);
            // Remove from local state
            setClients(prev => prev.filter(client => client.id !== clientId));
        } catch (err) {
            setError((err as Error).message || 'Failed to delete client');
        }
    };

    const getStatusIcon = (status: ClientStatus): string => {
        const icons: Record<ClientStatus, string> = {
            draft: '📝',
            financials: '⚡',
            completed: '✅',
            lenders_submissions: '📤',
            funding_booking: '💰'
        };

        return icons[status] || '📊';
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredClients = useMemo(() => {
        const statusFilter = TAB_TO_STATUS[activeTab];
        if (statusFilter === null) {
            return clients;
        }
        return clients.filter(client => client.status === statusFilter);
    }, [clients, activeTab]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner mb-4"></div>
                    <p className="text-gray-600">Loading clients...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-md">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
                            </div>
                            <button
                                onClick={onNewClient}
                                className="btn btn-primary"
                                type="button"
                            >
                                + New Client
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-4 border-b">
                            {(['all', 'draft', 'financials', 'lenders_submissions', 'funding_booking', 'final'] as TabFilter[]).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`pb-2 px-1 font-medium capitalize ${activeTab === tab
                                        ? 'text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    type="button"
                                >
                                    {TAB_NAMES[tab]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mx-6 mt-4 alert alert-error">
                            <strong>Error:</strong> {error}
                            <button
                                onClick={() => setError(null)}
                                className="ml-4 text-red-800 hover:text-red-900"
                                type="button"
                            >
                                ×
                            </button>
                        </div>
                    )}

                    {/* Clients List */}
                    <div className="p-6">
                        {filteredClients.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="text-gray-300 text-8xl mb-6">📊</div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                    {clients.length === 0
                                        ? 'No clients yet'
                                        : `No ${activeTab === 'all' ? '' : TAB_NAMES[activeTab].toLowerCase()} clients`
                                    }
                                </h3>
                                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                                    {clients.length === 0
                                        ? 'Get started by creating your first client. Upload your financial statements and get comprehensive credit analysis.'
                                        : `No clients match the "${TAB_NAMES[activeTab]}" filter. Try selecting a different tab.`
                                    }
                                </p>
                                {clients.length === 0 && (
                                    <button
                                        onClick={onNewClient}
                                        className="btn btn-primary text-lg px-8 py-3 font-medium"
                                        type="button"
                                    >
                                        ✨ Create Your First Client
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Industry</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Created</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Completed</th>
                                            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredClients.map((client) => (
                                            <tr
                                                key={client.id}
                                                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="py-4 px-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xl" title={`Status: ${client.status.replace('_', ' ')}`}>
                                                            {getStatusIcon(client.status)}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-2 h-2 rounded-full ${STATUS_BADGE_COLORS[client.status] || 'bg-gray-400'}`}></div>
                                                            <span className="text-xs font-medium text-gray-600">
                                                                {STATUS_BADGE_LABELS[client.status] || 'IN PROGRESS'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div>
                                                        <div className="font-semibold text-gray-900">{client.company_name}</div>
                                                        {client.status !== 'completed' && (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                {STATUS_PROGRESS_MESSAGES[client.status] || 'In progress'}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4 text-sm text-gray-600">
                                                    {client.industry}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-gray-600">
                                                    {formatDate(client.created_at)}
                                                </td>
                                                <td className="py-4 px-4 text-sm text-gray-600">
                                                    {client.completed_at ? formatDate(client.completed_at) : '-'}
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => onSelectClient(client.id)}
                                                            className={STATUS_BUTTON_CONFIG[client.status]?.className}
                                                            type="button"
                                                        >
                                                            {STATUS_BUTTON_CONFIG[client.status]?.label}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(client.id)}
                                                            className="btn btn-danger btn-sm font-bold"
                                                            type="button"
                                                        >
                                                            🗑️ Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientList;
